import {useEffect, useMemo, useRef, useState} from "react";
import {Box, IconButton, InputBase, Popover, Tooltip, Typography} from "@mui/material";
import FunctionsIcon from "@mui/icons-material/Functions";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import type {ElevationApp} from "./ElevationApp";
import {FrmVar} from "./drawfrm/FrmVar";
import {EventEmitter2} from "./util/EventEmitter2";
import {useEventEmitter2} from "./util/useEventEmitter2";
import {inches} from "./const";
import {frameBlocksDebugTree} from "./scenes/frameGrid/debugFrameBlocks";
import type {LayoutDebug} from "./scenes/frameGrid/Layout";

// A StatusBar affordance that opens a line-based dump of every current model
// variable and its formula (name = expr), plus the frame-grid solver's
// constraints from the last layout. Read-only — a window into what the solver
// and the Md* model are working with right now.
export function DebugVarsPopover(props: {app: EventEmitter2<ElevationApp | null>}) {
    const app = useEventEmitter2(props.app);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    if(!app) return null;

    return (
        <>
            <Tooltip title="Variables & formulas">
                <IconButton size="small" sx={{p: 0.25}} onClick={e => setAnchor(e.currentTarget)}>
                    <FunctionsIcon sx={{fontSize: "1rem"}} />
                </IconButton>
            </Tooltip>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                transformOrigin={{vertical: "bottom", horizontal: "right"}}
                PaperProps={{sx: {width: 460, maxHeight: "70vh"}}}
            >
                {!!anchor && <DebugVarsBody app={app} />}
            </Popover>
        </>
    );
}

function DebugVarsBody(props: {app: ElevationApp}) {
    const app = props.app;
    const [tick, setTick] = useState(0);
    const [filter, setFilter] = useState("");

    // re-read the model on every open and whenever a value changes; the Md* trees
    // are replaced wholesale on reset, so collect fresh instances each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const groups = useMemo(() => collectVars(app), [app, tick]);
    const layout = useEventEmitter2(app.debugLayout);

    // live-update while open: bump `tick` on any variable or layout change. New
    // vars created after this mount (e.g. a structural edit) get picked up on the
    // next value change that fires here.
    useEffect(() => {
        const vars = collectVars(app).flatMap(g => g.vars);
        const subs = vars.map(v => v.frmVar.subscribe(() => setTick(t => t + 1)));
        const layoutSub = app.debugLayout.subscribe(() => setTick(t => t + 1));
        return () => {
            subs.forEach(s => s.cancel());
            layoutSub();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app]);

    const needle = filter.trim().toLowerCase();
    const match = (s: string) => !needle || s.toLowerCase().includes(needle);

    // copy the currently-shown content (same filter applied) as plain text.
    const [copied, setCopied] = useState(false);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if(copyTimer.current) clearTimeout(copyTimer.current); }, []);
    const copy = () => {
        const text = renderPlainText(groups, layout, app, match, needle);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            if(copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
    };

    return (
        <Box sx={{fontSize: "0.72rem", fontFamily: "monospace"}}>
            <Box sx={{position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, px: 1, py: 0.5, borderBottom: 1, borderColor: "divider", display: "flex", gap: 0.5, alignItems: "center"}}>
                <InputBase
                    autoFocus
                    placeholder="filter…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    sx={{flex: 1, fontSize: "0.72rem", fontFamily: "monospace", px: 0.5, py: 0.25, border: 1, borderColor: "divider", borderRadius: 1}}
                />
                <Tooltip title={copied ? "Copied!" : "Copy as text"}>
                    <IconButton size="small" sx={{p: 0.25}} onClick={copy}>
                        {copied ? <CheckIcon sx={{fontSize: "1rem"}} /> : <ContentCopyIcon sx={{fontSize: "1rem"}} />}
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{px: 1, py: 0.5}}>
                {groups.map(g => {
                    const vars = g.vars.filter(v => match(v.path) || match(v.frmVar.name()) || match(v.frmVar.getFormulaDebug().formula || ""));
                    if(vars.length === 0) return null;
                    return (
                        <Box key={g.group} sx={{mb: 1}}>
                            <GroupHeader label={g.group} />
                            {vars.map(v => <VarLine key={v.path} entry={v} />)}
                        </Box>
                    );
                })}

                <LayoutSection app={app} layout={layout} needle={needle} match={match} />
            </Box>
        </Box>
    );
}

// the frame-grid layout as the nested JSON block tree (same shape as the console
// `zzDebugFrameBlocks` dump), followed by the solver's structural constraints from
// the last solve. Live: sizes come from the model's FrmVars, which the body
// subscribes to.
function LayoutSection(props: {
    app: ElevationApp;
    layout: LayoutDebug | null | undefined;
    needle: string;
    match: (s: string) => boolean;
}) {
    const {app, layout, needle, match} = props;

    const treeJson = JSON.stringify(frameBlocksDebugTree(app.frameSpec.frameBlocks), null, 2);
    const showTree = match("blocks") || match("layout") || treeJson.toLowerCase().includes(needle);
    const constraints = layout ? layout.constraints.filter(match) : [];
    const showConstraints = !!layout && (match("layout") || match("constraints") || constraints.length > 0);

    if(!showTree && !showConstraints) return null;

    return (
        <Box sx={{mb: 1}}>
            {showTree && (
                <>
                    <GroupHeader label="frame-grid blocks" />
                    <Box component="pre" sx={{
                        fontFamily: "monospace",
                        fontSize: "0.68rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        m: 0,
                        color: "text.secondary",
                    }}>
                        {treeJson}
                    </Box>
                </>
            )}
            {showConstraints && (
                <>
                    <GroupHeader label="constraints (last solve)" />
                    {constraints.map((c, i) => <Line key={i} left={c} />)}
                </>
            )}
        </Box>
    );
}

// serialize the currently-shown content to plain text, mirroring the on-screen
// rendering (VarLine / LayoutSection) and applying the same filter, so the copy
// matches what the user sees.
function renderPlainText(
    groups: VarGroup[],
    layout: LayoutDebug | null | undefined,
    app: ElevationApp,
    match: (s: string) => boolean,
    needle: string,
): string {
    const lines: string[] = [];

    for(const g of groups) {
        const vars = g.vars.filter(v => match(v.path) || match(v.frmVar.name()) || match(v.frmVar.getFormulaDebug().formula || ""));
        if(vars.length === 0) continue;

        lines.push(g.group);
        for(const v of vars) {
            const d = v.frmVar.getFormulaDebug();
            const name = label(v.path, d.name);
            const value = fmtVal(d.name, d.value);

            if(d.isCalculated && d.formula) {
                lines.push(`  ${name} = ${d.formula}  ${value}`);
                if(d.deps && d.deps.length > 0) {
                    lines.push(`    ${d.deps.map(dep => `${dep.prop}=${fmtVal(dep.name, dep.value)}`).join(", ")}`);
                }
            } else {
                lines.push(`  ${name} = ${d.isModified ? "* " : ""}${value}`);
            }
        }
        lines.push("");
    }

    const treeJson = JSON.stringify(frameBlocksDebugTree(app.frameSpec.frameBlocks), null, 2);
    if(match("blocks") || match("layout") || treeJson.toLowerCase().includes(needle)) {
        lines.push("frame-grid blocks");
        lines.push(treeJson);
        lines.push("");
    }

    const constraints = layout ? layout.constraints.filter(match) : [];
    if(!!layout && (match("layout") || match("constraints") || constraints.length > 0)) {
        lines.push("constraints (last solve)");
        constraints.forEach(c => lines.push(`  ${c}`));
        lines.push("");
    }

    return lines.join("\n").trimEnd() + "\n";
}

function GroupHeader(props: {label: string}) {
    return (
        <Typography sx={{fontFamily: "monospace", fontSize: "0.7rem", fontWeight: 700, color: "primary.main", mt: 0.5, mb: 0.25}}>
            {props.label}
        </Typography>
    );
}

function VarLine(props: {entry: VarEntry}) {
    const d = props.entry.frmVar.getFormulaDebug();
    const name = label(props.entry.path, d.name);
    const value = fmtVal(d.name, d.value);

    if(d.isCalculated && d.formula) {
        return (
            <Box sx={{mb: 0.25}}>
                <Line left={`${name} = ${d.formula}`} right={value} calc />
                {d.deps && d.deps.length > 0 && (
                    <Typography component="div" sx={{fontFamily: "monospace", fontSize: "0.65rem", color: "text.disabled", pl: 2}}>
                        {d.deps.map(dep => `${dep.prop}=${fmtVal(dep.name, dep.value)}`).join(", ")}
                    </Typography>
                )}
            </Box>
        );
    }

    return <Line left={`${name} =`} right={value} modified={d.isModified} />;
}

function Line(props: {left: string; right?: string; muted?: boolean; calc?: boolean; modified?: boolean}) {
    return (
        <Box sx={{display: "flex", gap: 1, alignItems: "baseline", justifyContent: "space-between"}}>
            <Typography component="span" sx={{
                fontFamily: "monospace",
                fontSize: props.muted ? "0.65rem" : "0.72rem",
                color: props.muted ? "text.disabled" : props.calc ? "secondary.main" : "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            }}>
                {props.left}
            </Typography>
            {props.right !== undefined && (
                <Typography component="span" sx={{fontFamily: "monospace", fontSize: "0.72rem", flexShrink: 0, fontWeight: 600}}>
                    {props.modified ? " *" : ""}{props.right}
                </Typography>
            )}
        </Box>
    );
}

// ---- model walk ------------------------------------------------------------

export type VarEntry = {path: string; frmVar: FrmVar};
export type VarGroup = {group: string; vars: VarEntry[]};

// walk each top-level Md* model and collect every FrmVar it (transitively) holds,
// keyed by field path — the debug name alone isn't unique (a handful of vars share
// one). Recurses through nested holders (the frameBlocks tree, door lites, hinges)
// with a visited set to guard against reference cycles.
export function collectVars(app: ElevationApp): VarGroup[] {
    return [
        {group: "frameSpec", vars: walk(app.frameSpec, "frameSpec")},
        {group: "doorSpec", vars: walk(app.doorSpec, "doorSpec")},
        {group: "hardware", vars: walk(app.hardware, "hardware")},
    ].filter(g => g.vars.length > 0);
}

function walk(root: any, rootLabel: string): VarEntry[] {
    const out: VarEntry[] = [];
    const seen = new WeakSet<object>();

    const visit = (val: any, path: string) => {
        if(val instanceof FrmVar) {
            out.push({path, frmVar: val});
            return;
        }
        if(val === null || typeof val !== "object") return;
        // skip machinery that isn't model data (and can hold closures / cycles).
        if(val instanceof EventEmitter2 || val instanceof Map || val instanceof Set) return;
        if(seen.has(val)) return;
        seen.add(val);

        if(Array.isArray(val)) {
            val.forEach((v, i) => visit(v, `${path}[${i}]`));
            return;
        }

        for(const key of Object.keys(val)) {
            visit(val[key], path ? `${path}.${key}` : key);
        }
    };

    visit(root, rootLabel);
    return out;
}

// ---- formatting ------------------------------------------------------------

// counts (e.g. hinge count) are stored as plain numbers, not drawing-unit lengths;
// everything else is a length in internal units.
function fmtVal(name: string, value: number): string {
    if(/count/i.test(name)) return String(Math.round(value));
    return (value/inches).toFixed(2) + '"';
}

// drop the redundant leading "<group>." from the debug name (the group header
// already shows it) while keeping specific ids intact for the block/lite/hinge
// tree vars, whose debug names aren't group-prefixed.
function label(path: string, debugName: string): string {
    const prefix = path.split(".")[0] + ".";
    return debugName.startsWith(prefix) ? debugName.slice(prefix.length) : debugName;
}
